import { uiText } from '../../utils/i18n';
import { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import type { FileAttachment } from '../../types';
import { fileExt, isTextPreviewable, isOfficePreviewable, serverPreviewUrl, formatFileSize } from './helpers';
import { downloadAuthenticatedFile, fetchAuthenticatedFile, useAuthenticatedBlobUrl } from '../../hooks/useAuthenticatedBlobUrl';
import { AuthenticatedImage } from '../shared/AuthenticatedImage';
import styles from './FilePreview.module.css';

interface FilePreviewProps {
  file: FileAttachment;
  onClose: () => void;
}

export function FilePreview({ file, onClose }: FilePreviewProps) {
  const [content, setContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const ext = fileExt(file.name);
  const isMarkdown = ext === 'md' || ext === 'markdown';
  const isHtml = ext === 'html' || ext === 'htm';
  const isImage = file.type.startsWith('image/');
  const isPdf = ext === 'pdf';
  const officeType = isOfficePreviewable(file.name);
  const isEmbed = isPdf || isHtml || !!officeType;
  const isText = isTextPreviewable(file.name, file.type) && !isHtml;
  const embedSource = officeType ? serverPreviewUrl(file.url) : file.url;
  const embedBlob = useAuthenticatedBlobUrl(isEmbed ? embedSource : undefined);

  useEffect(() => {
    if (isImage || isEmbed) {
      setLoading(false);
      return;
    }
    if (!isText) {
      setLoading(false);
      setError(uiText("此文件类型暂不支持预览"));
      return;
    }

    fetchAuthenticatedFile(file.url)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.text();
      })
      .then((text) => {
        setContent(text);
        setLoading(false);
      })
      .catch((err) => {
        setError((err as Error).message);
        setLoading(false);
      });
  }, [file.url, isText, isImage, isEmbed]);

  return (
    <div className={styles.container}>
      {/* Header bar */}
      <div className={styles.header}>
        <button className={styles.backBtn} onClick={onClose}>
          <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Back
        </button>
        <div className={styles.headerInfo}>
          <span className={styles.fileIcon}>{getFileIcon(ext)}</span>
          <span className={styles.fileName}>{file.name}</span>
          {file.size > 0 && (
            <span className={styles.fileMeta}>{formatFileSize(file.size)}</span>
          )}
        </div>
        <a
          className={styles.downloadBtn}
          href="#"
          onClick={(event) => { event.preventDefault(); void downloadAuthenticatedFile(file); }}
          title={uiText("下载")}
        >
          <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
        </a>
      </div>

      {/* Body */}
      <div className={styles.body}>
        {loading ? (
          <div className={styles.loading}>{uiText("加载中...")}</div>
        ) : error ? (
          <div className={styles.error}>{error}</div>
        ) : isImage ? (
          <div className={styles.imageWrap}>
            <AuthenticatedImage src={file.url} alt={file.name} className={styles.image} />
          </div>
        ) : isEmbed ? (
          embedBlob.loading ? <div className={styles.loading}>{uiText("加载中...")}</div>
            : embedBlob.error ? <div className={styles.error}>{embedBlob.error}</div>
              : <iframe src={embedBlob.blobUrl || undefined} sandbox="" className={styles.iframe} title={file.name} />
        ) : isMarkdown && content != null ? (
          <div className={styles.markdownContent}>
            <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeHighlight]}>
              {content}
            </ReactMarkdown>
          </div>
        ) : content != null ? (
          <pre className={styles.codeContent}>{content}</pre>
        ) : null}
      </div>
    </div>
  );
}

function getFileIcon(ext: string): string {
  const icons: Record<string, string> = {
    md: '📝', markdown: '📝', pdf: '📕', html: '🌐', htm: '🌐',
    json: '📋', xml: '📋', yaml: '📋', yml: '📋',
    js: '📜', ts: '📜', jsx: '📜', tsx: '📜', py: '📜',
    csv: '📊', xlsx: '📊', xls: '📊',
    docx: '📄', doc: '📄', txt: '📄',
    png: '🖼', jpg: '🖼', jpeg: '🖼', gif: '🖼', svg: '🖼', webp: '🖼',
  };
  return icons[ext] || '📎';
}
